import { meResponseSchema, type PublicUser } from '@hrms/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { ApiError, apiGet, apiSend, setUnauthorizedHandler } from '../lib/api.ts'

export type CurrentUser = PublicUser

export const ME_KEY = ['me'] as const

interface AuthState {
  user: CurrentUser | null
  status: 'loading' | 'ready' | 'error'
  /** Replace the signed-in user (after dev sign-in). */
  setUser: (user: CurrentUser | null) => void
  signOut: () => Promise<void>
  refetch: () => void
}

const AuthContext = createContext<AuthState | null>(null)

async function fetchMe(): Promise<CurrentUser | null> {
  try {
    return await apiGet('/me', (d) => meResponseSchema.parse(d).user)
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null
    throw err
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  })

  const setUser = useCallback(
    (user: CurrentUser | null) => {
      if (!user) {
        // Drop every cached response that belonged to the old session.
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] })
      }
      qc.setQueryData(ME_KEY, user)
    },
    [qc],
  )

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null))
    return () => setUnauthorizedHandler(null)
  }, [setUser])

  const signOut = useCallback(async () => {
    try {
      await apiSend('POST', '/auth/logout')
    } finally {
      setUser(null)
    }
  }, [setUser])

  const value = useMemo<AuthState>(
    () => ({
      user: me.data ?? null,
      status: me.isPending ? 'loading' : me.isError ? 'error' : 'ready',
      setUser,
      signOut,
      refetch: () => void me.refetch(),
    }),
    [me.data, me.isPending, me.isError, me.refetch, setUser, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/** The signed-in user. Only use under <RequireAuth> (the app shell), where it is never null. */
export function useCurrentUser(): CurrentUser {
  const { user } = useAuth()
  if (!user) throw new Error('useCurrentUser used outside an authenticated route')
  return user
}

export function useSignOut() {
  return useAuth().signOut
}
